import { z } from 'zod';
import { captureError } from '@/lib/sentry';
import {
  ORDER_STATUSES,
  ACTIVE_ORDER_STATUSES,
  type GuestOrderLookupResponse,
  type Order,
  type OrderListResponse,
  type OrderResponse,
  type PaymentStatus,
  type PaymentTimingKind,
} from './types';

const MenuOptionInputTypeSchema = z.enum([
  'single',
  'multiple',
  'required_single',
  'required_multiple',
  'text_input',
  'number_input',
  'textarea',
]);

const OptionalStringSchema = z.string().nullable().optional();

const OrderItemOptionDetailSchema = z.object({
  option_name: z.string(),
  choices: z.array(z.string()),
  price_modifier: z.number(),
}).passthrough();

const OrderItemSelectedOptionSchema = z.object({
  id: z.number(),
  menu_option_group_id: z.number(),
  menu_option_id: z.number().nullable(),
  group_name: z.string(),
  option_name: z.string().nullable(),
  input_value: z.string().nullable(),
  price: z.number(),
  input_type: MenuOptionInputTypeSchema,
  is_selection_type: z.boolean(),
  display_value: z.string(),
}).passthrough();

export const OrderItemDetailSchema = z.object({
  id: z.number(),
  menu_id: z.number(),
  menu_name: z.string(),
  quantity: z.number(),
  unit_price: z.number(),
  total_price: z.number(),
  options: z.array(OrderItemOptionDetailSchema).nullable().optional(),
  selected_options: z.array(OrderItemSelectedOptionSchema).optional(),
  special_instructions: OptionalStringSchema,
}).passthrough();

const OrderStatusHistorySchema = z.object({
  id: z.number(),
  from_status: z.enum(ORDER_STATUSES).nullable(),
  to_status: z.enum(ORDER_STATUSES),
  changed_by: z.string(),
  reason: z.string().nullable(),
  changed_at: z.string(),
}).passthrough();

const StoreSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: OptionalStringSchema,
  pickup_location: z.string().nullable().optional(),
}).passthrough();

const PaginationLinksSchema = z.object({
  first: z.string().nullable(),
  last: z.string().nullable(),
  prev: z.string().nullable(),
  next: z.string().nullable(),
}).passthrough();

const PaginationMetaSchema = z.object({
  current_page: z.number(),
  from: z.number().nullable(),
  last_page: z.number(),
  per_page: z.number(),
  to: z.number().nullable(),
  total: z.number(),
}).passthrough();

export const OrderSchema = z.object({
  id: z.number(),
  order_number: z.string().nullable(),
  // #1919 P4: 영업일 내 POS 짧은 순번(#N) — legacy/TEMP 는 null/부재.
  daily_sequence: z.number().int().nullable().optional(),
  store_id: z.number(),
  store_name: z.string(),
  order_type: z.enum(['pickup', 'table']),
  order_type_label: OptionalStringSchema,
  table_number: z.string().nullable().optional(),
  status: z.enum(ORDER_STATUSES),
  status_label: OptionalStringSchema,
  status_color: OptionalStringSchema,
  items: z.array(OrderItemDetailSchema),
  gross_subtotal: z.number(),
  net_subtotal: z.number(),
  tax_rate: z.number().optional(),
  tax: z.number(),
  tip: z.number().nullable().optional(),
  total: z.number(),
  currency: z.string(),
  customer_name: z.string().nullable().optional(),
  customer_phone: z.string().nullable().optional(),
  guest_phone: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  estimated_ready_time: z.string().nullable().optional(),
  confirmed_at: z.string().nullable().optional(),
  preparing_at: z.string().nullable().optional(),
  ready_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  processed_at: z.string().nullable().optional(),
  cancellation_reason: z.string().nullable().optional(),
  cancelled_at: z.string().nullable().optional(),
  cancelled_by: z.string().nullable().optional(),
  cancelled_by_label: z.string().nullable().optional(),
  refund_status: z.enum(['none', 'pending', 'partial', 'full', 'not_required', 'processing', 'completed', 'failed']).nullable().optional(),
  refund_status_label: z.string().nullable().optional(),
  failure_reason: z.string().nullable().optional(),
  status_history: z.array(OrderStatusHistorySchema).optional(),
  is_processing: z.boolean().optional(),
  is_active: z.boolean().optional(),
  is_customer_cancellable: z.boolean().optional(),
  item_count: z.number().optional(),
  store: StoreSummarySchema.optional(),
  pickup_location: z.string().nullable().optional(),
  // C2 (#590): malformed payload 통과 방지 — latest_payment 필드 zod 검증
  // #590 C2: backend 신규 결제상태/timing 값에도 주문목록이 깨지지 않도록 값은 lenient 검증
  // (배지 resolver 가 unknown graceful 처리). TS union(PaymentStatus 등)은 컴파일타임 안전 유지.
  // transform 으로 string → PaymentStatus / PaymentTimingKind 캐스트 (런타임 lenient, 타입 strict 분리)
  latest_payment: z.object({
    status: z.string().min(1),
    is_paid: z.boolean(),
    timing: z.string().nullable(),
    combination_code: z.string().nullable(),
    paid_at: z.string().nullable(),
  }).transform((v) => ({
    status: v.status as PaymentStatus,
    is_paid: v.is_paid,
    timing: v.timing as PaymentTimingKind | null,
    combination_code: v.combination_code,
    paid_at: v.paid_at,
  })).nullable().optional(),
}).passthrough();

const CreateOrderSchema = OrderSchema.extend({
  store_name: z.string().optional(),
}).transform((order): Order => ({
  ...order,
  store_name: order.store_name ?? '',
}));

export const OrderResponseSchema = z.object({
  data: OrderSchema,
}).passthrough();

export const CreateOrderResponseSchema = z.object({
  data: CreateOrderSchema,
}).passthrough();

export const OrderListResponseSchema = z.object({
  data: z.array(OrderSchema),
  links: PaginationLinksSchema.optional(),
  meta: PaginationMetaSchema.optional(),
}).passthrough();

const FullOrderOnlyKeys = [
  'store_id',
  'order_type',
  'items',
  'gross_subtotal',
  'net_subtotal',
  'tax',
] as const;

function getFullOrderOnlyKeys(payload: Record<string, unknown>): string[] {
  return FullOrderOnlyKeys.filter((key) =>
    Object.prototype.hasOwnProperty.call(payload, key)
  );
}

const GuestOrderSummarySchema = z.object({
  id: z.number(),
  order_number: z.string().nullable(),
  daily_sequence: z.number().int().nullable().optional(),
  store_name: z.string(),
  status: z.enum(ORDER_STATUSES),
  status_label: OptionalStringSchema,
  total: z.number(),
  currency: z.string(),
  created_at: z.string(),
}).passthrough().superRefine((order, ctx) => {
  const fullOrderKeys = getFullOrderOnlyKeys(order);

  if (fullOrderKeys.length > 0) {
    ctx.addIssue({
      code: 'custom',
      message: `Full order payload must satisfy OrderSchema before summary fallback (${fullOrderKeys.join(', ')})`,
    });
  }
}).transform((order): Order => ({
  ...order,
  store_id: 0,
  order_type: 'pickup',
  items: [],
  gross_subtotal: order.total,
  net_subtotal: order.total,
  tax: 0,
  updated_at: order.created_at,
}));

export const GuestOrderLookupSchema = z.object({
  data: z.array(z.union([OrderSchema, GuestOrderSummarySchema])),
  message: z.string().default(''),
}).passthrough();

const ActiveStatusSchema = z.enum(ACTIVE_ORDER_STATUSES);
const ActiveOrderBatchSchema = z.object({
  data: z.array(OrderSchema).max(250),
  meta: z.object({
    contract: z.literal('customer-active-orders.v1'),
    per_status_limit: z.literal(50),
    requested_statuses: z.array(ActiveStatusSchema).length(5),
    succeeded_statuses: z.array(ActiveStatusSchema).min(1).max(5),
    failed_statuses: z.array(ActiveStatusSchema).max(5),
  }),
}).superRefine(({ data, meta }, ctx) => {
  const requested = new Set(meta.requested_statuses);
  const accounted = [...meta.succeeded_statuses, ...meta.failed_statuses];
  if (requested.size !== 5 || accounted.length !== 5 || new Set(accounted).size !== 5) {
    ctx.addIssue({ code: 'custom', message: 'Active status accounting must be complete and disjoint' });
  }
  const counts = new Map<string, number>();
  for (const order of data) {
    const count = (counts.get(order.status) ?? 0) + 1;
    counts.set(order.status, count);
    if (!meta.succeeded_statuses.some(status => status === order.status) || count > 50) {
      ctx.addIssue({ code: 'custom', message: 'Order rows must belong to successful capped status buckets' });
      break;
    }
  }
});

export function parseActiveOrderBatchResponse(response: unknown) {
  return parseOrderApiResponse(ActiveOrderBatchSchema, response, 'getActiveOrdersBatch');
}

function formatIssues(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join('.') : '<root>';
      return `${path}: ${issue.message}`;
    })
    .join('; ');
}

function parseOrderApiResponse<T>(
  schema: z.ZodType<T>,
  response: unknown,
  context: string
): T {
  const result = schema.safeParse(response);
  if (result.success) {
    return result.data;
  }

  const error = new Error(
    `[orderApi] Invalid ${context} response shape: ${formatIssues(result.error)}`
  );
  captureError(error, {
    api: 'orderApi',
    context,
    issues: result.error.issues.map((issue) => ({
      path: issue.path.join('.'),
      code: issue.code,
      message: issue.message,
    })),
  });
  throw error;
}

export function parseOrderResponse(response: unknown, context: string): OrderResponse {
  return parseOrderApiResponse(OrderResponseSchema, response, context);
}

export function parseCreateOrderResponse(response: unknown, context: string): OrderResponse {
  return parseOrderApiResponse(CreateOrderResponseSchema, response, context);
}

export function parseOrderListResponse(response: unknown, context: string): OrderListResponse {
  return parseOrderApiResponse(OrderListResponseSchema, response, context);
}

export function parseGuestOrderLookupResponse(
  response: unknown,
  context: string
): GuestOrderLookupResponse {
  return parseOrderApiResponse(GuestOrderLookupSchema, response, context);
}
